import { Flex, Spinner } from "@appsmith/wds";
import React, { useEffect, useRef } from "react";

import { ChatMessageItem, type ChatMessageItemProps } from "../ChatMessageItem";
import styles from "./styles.module.css";
import { type Message } from "../types";
import type { ToolCall } from "../types/toolCall";
import { ChatResponseLoadingIndicator } from "../ChatResponseLoadingIndicator";
import {
  ChatMessageListContainer,
  type ChatMessageListContainerRef,
} from "./Container";
import { useUserTurnMessages } from "./useUserTurnMessages";
interface ChatThreadProps
  extends Pick<ChatMessageItemProps, "onCitationOpen" | "selectedCitationId"> {
  thread: Message[];
  isThreadLoading?: boolean;
  isWaitingForResponse: boolean;
  onApplyAssistantSuggestion?: (suggestion: string) => void;
  onToolCallApprove: (messageId: string, toolCallId: string) => void;
  onToolCallReject: (messageId: string, toolCallId: string) => void;
  onToolCallSelect: (toolCall: ToolCall) => void;
}

export const ChatMessageList = (props: ChatThreadProps) => {
  const {
    isThreadLoading,
    isWaitingForResponse,
    onApplyAssistantSuggestion,
    onCitationOpen,
    onToolCallApprove,
    onToolCallReject,
    onToolCallSelect,
    selectedCitationId,
    thread,
  } = props;
  const containerRef = useRef<ChatMessageListContainerRef>(null);
  const userTurns = useUserTurnMessages(thread);

  useEffect(
    // We scroll to bottom when new user turns are added
    function scrollToBottom() {
      if (containerRef.current) {
        containerRef.current.scrollToBottom();
      }
    },
    [userTurns.length],
  );

  if (Boolean(isThreadLoading)) {
    return (
      <Flex
        alignItems="center"
        data-testid="t--ai-chat-loading-indicator-wrapper"
        flexGrow={1}
        isInner
        justifyContent="center"
        padding="spacing-5"
      >
        <Spinner />
      </Flex>
    );
  }

  return (
    <ChatMessageListContainer
      // For a single message, we show the message on the bottom
      messagesDirection={thread.length > 1 ? "column" : "column-reverse"}
      ref={containerRef}
    >
      {/* This generally happens when there are no user messages and we show a
       single assistant welcome message */}
      {userTurns.length === 0 && thread.length > 0
        ? thread.map((message, index) => (
            <ChatMessageItem
              dataTestId={`t--ai-chat-message-item-${message.id}`}
              isAssistantSuggestionVisible={index === thread.length - 1}
              key={message.id}
              message={message}
              onApplyAssistantSuggestion={onApplyAssistantSuggestion}
              onCitationOpen={onCitationOpen}
              onToolCallApprove={onToolCallApprove}
              onToolCallReject={onToolCallReject}
              onToolCallSelect={onToolCallSelect}
              selectedCitationId={selectedCitationId}
            />
          ))
        : userTurns.map((turn, index) => {
            const isCurrentUserTurn = index === userTurns.length - 1;

            return (
              <div
                className={styles.messageTurn}
                data-testid={`t--ai-chat-message-turn-${turn.userMessage.id}`}
                key={turn.userMessage.id}
              >
                {turn.userMessage && (
                  <ChatMessageItem
                    dataTestId={`t--ai-chat-message-item-${turn.userMessage.id}`}
                    isAssistantSuggestionVisible={false}
                    key={turn.userMessage.id}
                    message={turn.userMessage}
                    onApplyAssistantSuggestion={onApplyAssistantSuggestion}
                    onCitationOpen={onCitationOpen}
                    onToolCallApprove={onToolCallApprove}
                    onToolCallReject={onToolCallReject}
                    onToolCallSelect={onToolCallSelect}
                    selectedCitationId={selectedCitationId}
                  />
                )}
                {turn.assistantMessages.map((message, index) => (
                  <ChatMessageItem
                    dataTestId={`t--ai-chat-message-item-${message.id}`}
                    isAssistantSuggestionVisible={
                      index === turn.assistantMessages.length - 1 &&
                      isCurrentUserTurn
                    }
                    key={message.id}
                    message={message}
                    onApplyAssistantSuggestion={onApplyAssistantSuggestion}
                    onCitationOpen={onCitationOpen}
                    onToolCallApprove={onToolCallApprove}
                    onToolCallReject={onToolCallReject}
                    onToolCallSelect={onToolCallSelect}
                    selectedCitationId={selectedCitationId}
                  />
                ))}
                {isWaitingForResponse && isCurrentUserTurn ? (
                  <Flex
                    data-testid="t--ai-chat-loading-indicator-wrapper"
                    isInner
                    padding="spacing-4"
                  >
                    <ChatResponseLoadingIndicator />
                  </Flex>
                ) : null}
              </div>
            );
          })}
    </ChatMessageListContainer>
  );
};
